import { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";
import { SERVICES } from "../lib/constants";
import useReveal from "../hooks/useReveal";

export default function OurWork() {
  const head = useReveal();
  const [lightbox, setLightbox] = useState(null); // { serviceIdx, imgIdx }

  const openLightbox = (serviceIdx, imgIdx) =>
    setLightbox({ serviceIdx, imgIdx });
  const closeLightbox = useCallback(() => setLightbox(null), []);

  const next = useCallback(() => {
    if (!lightbox) return;
    const imgs = SERVICES[lightbox.serviceIdx].images;
    setLightbox((p) => ({ ...p, imgIdx: (p.imgIdx + 1) % imgs.length }));
  }, [lightbox]);

  const prev = useCallback(() => {
    if (!lightbox) return;
    const imgs = SERVICES[lightbox.serviceIdx].images;
    setLightbox((p) => ({
      ...p,
      imgIdx: (p.imgIdx - 1 + imgs.length) % imgs.length,
    }));
  }, [lightbox]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [lightbox, closeLightbox, next, prev]);

  return (
    <section
      id="work"
      data-testid="our-work-section"
      className="py-20 md:py-28 bg-[#F8FAFC]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          ref={head.ref}
          className={`fade-in-up ${head.visible ? "is-visible" : ""} max-w-2xl`}
        >
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00B4D8]">
            Our Work
          </span>
          <h2 className="mt-3 font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] tracking-tight">
            Two services. One reliable crew.
          </h2>
          <p className="mt-4 text-base lg:text-lg text-slate-500 leading-relaxed">
            Click any photo to enlarge. Browse real projects from our cleaning
            and installation teams across Tampa Bay.
          </p>
        </div>

        <div className="mt-14 space-y-16">
          {SERVICES.map((service, sIdx) => (
            <ServiceBlock
              key={service.id}
              service={service}
              serviceIdx={sIdx}
              onOpen={openLightbox}
            />
          ))}
        </div>
      </div>

      {lightbox && (
        <Lightbox
          service={SERVICES[lightbox.serviceIdx]}
          imgIdx={lightbox.imgIdx}
          onClose={closeLightbox}
          onNext={next}
          onPrev={prev}
        />
      )}
    </section>
  );
}

function ServiceBlock({ service, serviceIdx, onOpen }) {
  const { ref, visible } = useReveal();
  return (
    <div
      ref={ref}
      data-testid={`service-block-${service.id}`}
      className={`fade-in-up ${visible ? "is-visible" : ""}`}
    >
      {/* Service header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <span className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.2em] uppercase text-[#00B4D8]">
            <span className="w-8 h-px bg-[#00B4D8]" />
            Service · {String(serviceIdx + 1).padStart(2, "0")}
          </span>
          <h3 className="mt-2 font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-[#0A192F] tracking-tight">
            {service.title}
          </h3>
          <p className="mt-2 text-slate-500 max-w-2xl">{service.description}</p>
        </div>
      </div>

      {service.layout === "pairs" ? (
        <PairsLayout service={service} serviceIdx={serviceIdx} onOpen={onOpen} />
      ) : (
        <GridLayout service={service} serviceIdx={serviceIdx} onOpen={onOpen} />
      )}
    </div>
  );
}

// ----- Cleaning: Before / After pairs -----
function PairsLayout({ service, serviceIdx, onOpen }) {
  // Group images into pairs of 2
  const pairs = [];
  for (let i = 0; i < service.images.length; i += 2) {
    pairs.push([service.images[i], service.images[i + 1]]);
  }

  return (
    <div className="space-y-6 md:space-y-8">
      {pairs.map((pair, pIdx) => (
        <div
          key={pIdx}
          data-testid={`pair-row-${service.id}-${pIdx}`}
          className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-5"
        >
          {pair.map((img, j) => {
            if (!img) return null;
            const globalIdx = pIdx * 2 + j;
            return (
              <PairCard
                key={globalIdx}
                img={img}
                serviceId={service.id}
                index={globalIdx}
                pairNumber={pIdx + 1}
                onClick={() => onOpen(serviceIdx, globalIdx)}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}

function PairCard({ img, serviceId, index, pairNumber, onClick }) {
  const isAfter = img.label === "After";
  return (
    <button
      type="button"
      data-testid={`gallery-${serviceId}-${index}`}
      onClick={onClick}
      className="relative overflow-hidden rounded-2xl bg-[#0A192F] group cursor-zoom-in shadow-sm hover:shadow-2xl hover:shadow-[#0A192F]/15 transition-shadow duration-500 aspect-[4/3]"
    >
      <img
        src={img.url}
        alt={`${img.label} cleaning — pair ${pairNumber}`}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/55 via-transparent to-transparent" />

      {/* Before / After label chip */}
      <div
        className={`absolute top-3 left-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold tracking-[0.18em] uppercase shadow-md backdrop-blur-md border ${
          isAfter
            ? "bg-[#00B4D8] text-white border-[#00B4D8]"
            : "bg-white/95 text-[#0A192F] border-white/80"
        }`}
      >
        {img.label}
      </div>

      {/* Zoom indicator */}
      <div className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all">
        <ZoomIn className="w-4 h-4 text-[#0A192F]" />
      </div>

      {/* Pair number footer */}
      <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
        <span className="text-white/90 text-xs font-semibold tracking-wide">
          Project · {String(pairNumber).padStart(2, "0")}
        </span>
      </div>
    </button>
  );
}

// ----- Installation: tidy responsive grid -----
function GridLayout({ service, serviceIdx, onOpen }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-5">
      {service.images.map((img, i) => (
        <button
          key={i}
          type="button"
          data-testid={`gallery-${service.id}-${i}`}
          onClick={() => onOpen(serviceIdx, i)}
          className="relative overflow-hidden rounded-2xl bg-[#0A192F] group cursor-zoom-in shadow-sm hover:shadow-2xl hover:shadow-[#0A192F]/15 transition-shadow duration-500 aspect-[4/3]"
        >
          <img
            src={img.url}
            alt={`${service.shortTitle} — Photo ${i + 1}`}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/80 via-[#0A192F]/15 to-transparent opacity-70 group-hover:opacity-95 transition-opacity" />
          <div className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all">
            <ZoomIn className="w-4 h-4 text-[#0A192F]" />
          </div>
          <div className="absolute inset-x-0 bottom-0 p-3 sm:p-4 flex items-end">
            <p className="text-white font-heading font-semibold text-sm sm:text-base leading-snug text-left">
              {service.shortTitle} · Photo {i + 1}
            </p>
          </div>
        </button>
      ))}
    </div>
  );
}

// ----- Shared lightbox -----
function Lightbox({ service, imgIdx, onClose, onNext, onPrev }) {
  const img = service.images[imgIdx];
  return (
    <div
      data-testid="lightbox-modal"
      className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 animate-in fade-in"
      onClick={onClose}
    >
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-4 sm:p-6 z-10">
        <div className="text-white">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#00B4D8]">
            {service.shortTitle}
            {img.label ? ` · ${img.label}` : ""}
          </p>
          <p className="text-sm text-white/70 mt-0.5">
            Photo {imgIdx + 1} of {service.images.length}
          </p>
        </div>
        <button
          data-testid="lightbox-close"
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 flex items-center justify-center text-white transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <img
        src={img.url}
        alt={`${service.title} ${imgIdx + 1}`}
        className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />

      {service.images.length > 1 && (
        <>
          <button
            data-testid="lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              onPrev();
            }}
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 flex items-center justify-center text-white transition-colors"
            aria-label="Previous"
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          <button
            data-testid="lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 flex items-center justify-center text-white transition-colors"
            aria-label="Next"
          >
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </>
      )}
    </div>
  );
}
